import { useEffect, useState } from "react";
import { Download, X } from "lucide-react";

const DISMISS_KEY = "adt_pwa_dismissed_at";
const DISMISS_DAYS = 14;

export default function PWAInstallBanner() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Hide if user dismissed it within the last DISMISS_DAYS
    try {
      const ts = parseInt(localStorage.getItem(DISMISS_KEY) || "0", 10);
      if (ts && Date.now() - ts < DISMISS_DAYS * 86400 * 1000) return;
    } catch { /* ignore */ }

    // If already installed (running in standalone), skip
    if (window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone) return;

    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      // Slight delay so the banner doesn't fight LCP
      setTimeout(() => setVisible(true), 4000);
    };
    window.addEventListener("beforeinstallprompt", handler);

    const onInstalled = () => {
      setVisible(false);
      setDeferredPrompt(null);
      try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* ignore */ }
    };
    window.addEventListener("appinstalled", onInstalled);

    return () => {
      window.removeEventListener("beforeinstallprompt", handler);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const onInstall = async () => {
    if (!deferredPrompt) return;
    try {
      deferredPrompt.prompt();
      await deferredPrompt.userChoice;
    } catch { /* ignore */ }
    setDeferredPrompt(null);
    setVisible(false);
  };

  const onDismiss = () => {
    setVisible(false);
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* ignore */ }
  };

  if (!visible) return null;

  return (
    <div
      className="fixed bottom-4 left-4 right-4 md:left-auto md:right-6 md:max-w-md z-[60] bg-white border border-[hsl(var(--border))] shadow-xl rounded-lg p-4 flex items-start gap-3"
      role="dialog"
      aria-label="Uygulamayı yükle"
      data-testid="pwa-install-banner"
    >
      <div className="w-10 h-10 rounded-md bg-[hsl(var(--ink))] flex items-center justify-center flex-shrink-0">
        <Download className="w-5 h-5 text-white" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-medium text-sm">Adalardatatil&apos;i ana ekrana ekleyin</p>
        <p className="text-xs text-[hsl(var(--muted-fg))] mt-0.5 leading-relaxed">
          Tek dokunuşla feribot, otel ve tur biletlerinize ulaşın. Daha hızlı açılır, internet olmadan da görüntülenir.
        </p>
        <div className="mt-3 flex items-center gap-2">
          <button
            onClick={onInstall}
            className="px-3 py-1.5 text-xs font-medium bg-[hsl(var(--ink))] text-white rounded-md hover:opacity-90 transition-opacity"
            data-testid="pwa-install-button"
          >
            Yükle
          </button>
          <button
            onClick={onDismiss}
            className="px-3 py-1.5 text-xs text-[hsl(var(--muted-fg))] hover:text-[hsl(var(--ink))] transition-colors"
            data-testid="pwa-dismiss-button"
          >
            Şimdi değil
          </button>
        </div>
      </div>
      <button
        onClick={onDismiss}
        className="text-[hsl(var(--muted-fg))] hover:text-[hsl(var(--ink))] transition-colors flex-shrink-0"
        aria-label="Kapat"
        data-testid="pwa-close-button"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
